/**
 * React Query hook for tenants list
 *
 * Provides data fetching for tenants with pagination and filtering.
 */

import { useQuery } from '@tanstack/react-query';
import { TenantApiService } from '../services/tenantApi';
import type { TenantListItem, TenantStatus } from '../types/tenant';

/**
 * Query key factory for tenants
 */
export const tenantsKeys = {
  all: ['tenants'] as const,
  list: (params: UseTenantsParams) => ['tenants', 'list', params] as const,
};

/**
 * Parameters for useTenants hook
 */
export interface UseTenantsParams {
  page?: number;
  pageSize?: number;
  search?: string;
  status?: TenantStatus;
}

/**
 * Hook return type for useTenants
 */
export interface UseTenantsResult {
  tenants: TenantListItem[];
  totalPages: number;
  totalTenants: number;
  isLoading: boolean;
  isFetching: boolean;
  error: Error | null;
  refetch: () => Promise<void>;
}

/**
 * React Query hook for fetching tenants list
 *
 * @param params - Pagination and filter parameters
 * @returns Tenants data, pagination info, loading states, and refetch function
 *
 * @example
 * ```tsx
 * const { tenants, totalPages, isLoading, refetch } = useTenants({
 *   page: 1,
 *   pageSize: 20,
 *   search: 'acme',
 *   status: 'managed',
 * });
 * ```
 */
export function useTenants(params: UseTenantsParams): UseTenantsResult {
  const {
    data,
    isLoading,
    isFetching,
    error,
    refetch: queryRefetch,
  } = useQuery({
    queryKey: tenantsKeys.list(params),
    queryFn: async () => {
      const response = await TenantApiService.listTenants({
        page: params.page,
        page_size: params.pageSize,
        search: params.search,
        status: params.status,
      });
      return response;
    },
  });

  const refetch = async () => {
    await queryRefetch();
  };

  return {
    tenants: data?.items ?? [],
    totalPages: data?.total_pages ?? 0,
    totalTenants: data?.total ?? 0,
    isLoading,
    isFetching,
    error: error as Error | null,
    refetch,
  };
}
